import type { CaseId, Pt, WallResult } from "@tasduvar/engine";
import { toSvg, type Transform } from "../geometry";
import { pressurePlanes } from "../pressurePlanes";

/**
 * Basınç dağılımı: sanal düzlem boyunca (statik üçgen eşdeğeri + dinamik artım) ya da back-face yönteminde
 * her kademe arka yüzü boyunca (o yüzün bileşkelerinden yamuk eşdeğeri), düzlem/yüz normaline dik çizilir.
 * Görsel amaçlıdır; sayısal değerler sonuç panelindeki kuvvet tablosundan okunur. Düzlem verisi `pressurePlanes` (3B ile ortak).
 */
export function PressureDiagram({ t, result, caseId }: { t: Transform; result: WallResult; caseId: CaseId }): JSX.Element | null {
  const pp = pressurePlanes(result, caseId); if (!pp) return null;
  const { planes, pMax, Ka } = pp;
  const Href = result.geometry.Hp;
  const px = (p: number): number => (0.08 * Href * t.scale * p) / pMax;   // px cinsinden dışa uzaklık

  return (
    <g>
      {planes.map((p, i) => {
        const dx = p.top.x - p.bot.x, dy = p.top.y - p.bot.y, L = Math.hypot(dx, dy);
        const nx = dy / L, ny = -dx / L;                       // düzlem normali (dolgu tarafına)
        const off = (pt: Pt, d: number) => { const s = toSvg(t, pt); return { x: s.x + nx * d, y: s.y - ny * d }; };
        const quad = (bIn: number, tIn: number, bOut: number, tOut: number): string => {
          const a = off(p.bot, px(bIn)), b = off(p.top, px(tIn)), c2 = off(p.top, px(tOut)), d = off(p.bot, px(bOut));
          return `M ${a.x} ${a.y} L ${b.x} ${b.y} L ${c2.x} ${c2.y} L ${d.x} ${d.y} Z`;
        };
        const along = (s: number): Pt => ({ x: p.bot.x + dx * s, y: p.bot.y + dy * s });
        return (
          <g key={i}>
            <path data-part="pressure-static" d={quad(0, 0, p.sBot, p.sTop)} fill="#EA580C" fillOpacity={0.18} stroke="#EA580C" strokeWidth={1} />
            {p.dBot > 1e-9 ? <path data-part="pressure-dynamic" d={quad(p.sBot, p.sTop, p.sBot + p.dBot, p.sTop + p.dTop)} fill="#C62828" fillOpacity={0.18} stroke="#C62828" strokeWidth={1} /> : null}
            {i === 0 ? <text {...off(along(0.25), px(pMax) + 10)} className="text-[10px] fill-orange-700 num">K_a = {Ka.toFixed(3).replace(".", ",")}</text> : null}
          </g>
        );
      })}
    </g>
  );
}
